import { createContext, useState } from "react";

import { useSessionStorage } from "@react-hooks-library/core";
import {
  getUserMeGroups,
  postAuthAddPhoneRs,
  postAuthLoginRs,
  postAuthLogoutRs,
  postAuthResendCodeRs,
  postAuthVerifyOtpRs,
} from "@rubi/api-generated";
import { sanitizeRedirectPath } from "@rubi/utils";

import checkPermission, { Action, Resource } from "@/alert-permission";
import { apiUserClient, setApiRegion } from "@/api";
import { IAlertGroup, IUser } from "@/types";

export interface IAuthStateContext {
  currentUser: IUser | null;
  setCurrentUser: React.Dispatch<React.SetStateAction<IUser | null>>;
  currentUserAgency: IAlertGroup;
  setCurrentUserAgency: React.Dispatch<React.SetStateAction<IAlertGroup>>;
  currentUserGroups: IAlertGroup[];
  setCurrentUserGroups: React.Dispatch<React.SetStateAction<IAlertGroup[]>>;
  mfaUserID: string;
  stytchMemberID: string;
  intermediateToken: string;
  redactedNumber: string;
  setRedactedNumber: (value: string) => void;
  setMFAUserID: (value: string) => void;
  setMFAMethodID: (value: string) => void;
  authSteps: string[];
  checkUserAuthStatus: (redirect: boolean) => void;
  login: (email: string, password: string) => Promise<void>;
  setPassword: (token: string, password: string) => Promise<void>;
  logout: () => void;
  goBack: () => void;
  addPhoneNumber: (phoneNumber: string) => Promise<void>;
  verifyOTP: (otp: string) => Promise<void>;
  resendOTP: () => Promise<void>;
  verifyPasswordStrength: (password: string) => Promise<void>;
  redirectionPath?: string;
  setRedirectionPath?: (value: string) => void;
  getOrgIDFromURL: () => string;
  checkPermission: (
    action: Action,
    resource: Resource,
    groupId?: number,
  ) => boolean;
  refreshUserGroups: () => Promise<void>;
}

const AuthStateContext = createContext<IAuthStateContext>({
  currentUser: null as IUser | null,
  setCurrentUser: () => {},
  mfaUserID: "",
  stytchMemberID: "",
  intermediateToken: "",
  redactedNumber: "",
  setRedactedNumber: () => {},
  setMFAUserID: () => {},
  setMFAMethodID: () => {},
  authSteps: [],
  currentUserAgency: {} as IAlertGroup,
  setCurrentUserAgency: () => {},
  currentUserGroups: [],
  setCurrentUserGroups: () => {},
  checkUserAuthStatus: () => {},
  login: () => {
    return new Promise<void>(() => {});
  },
  setPassword: () => {
    return new Promise<void>(() => {});
  },
  logout: () => {},
  goBack: () => {},
  addPhoneNumber: () => {
    return new Promise<void>(() => {});
  },
  verifyOTP: () => {
    return new Promise<void>(() => {});
  },
  resendOTP: () => {
    return new Promise<void>(() => {});
  },
  verifyPasswordStrength: () => {
    return new Promise<void>(() => {});
  },
  redirectionPath: "",
  setRedirectionPath: () => {},
  getOrgIDFromURL: () => "",
  checkPermission: () => false,
  refreshUserGroups: () => {
    return new Promise<void>(() => {});
  },
});

interface IAuthStateProviderProps {
  children: React.ReactNode;
}

const AuthStateProvider = ({ children }: IAuthStateProviderProps) => {
  const [currentUser, setCurrentUser] = useState<IUser | null>(null);
  const [currentUserAgency, setCurrentUserAgency] = useState<IAlertGroup>(
    {} as IAlertGroup,
  );
  const [currentUserGroups, setCurrentUserGroups] = useState<IAlertGroup[]>([]);
  const [mfaUserID, setMFAUserID] = useSessionStorage<string>("mfaUserID", "");
  const [redactedNumber, setRedactedNumber] = useSessionStorage<string>(
    "redactedNumber",
    "",
  );
  const [redirectionPath, setRedirectionPath] = useSessionStorage<string>(
    "redirectionPath",
    "",
  );
  const [mfaMethodID, setMFAMethodID] = useSessionStorage<string>(
    "mfaMethodID",
    "",
  );
  const [stytchMemberID, setStytchMemberID] = useSessionStorage<string>(
    "stytchMemberID",
    "",
  );
  const [intermediateToken, setIntermediateToken] = useSessionStorage<string>(
    "intermediateToken",
    "",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [authSteps, setAuthSteps] = useSessionStorage<string[]>(
    "authSteps",
    [],
  );

  // Wrapper for checkPermission to provide current user and groups
  const checkUserPermission = (
    action: Action,
    resource: Resource,
    groupId?: number,
  ) => {
    return checkPermission(
      currentUser,
      action,
      resource,
      currentUserGroups,
      groupId,
    );
  };

  function goToNextStep() {
    const currentStep = authSteps.findIndex((step) =>
      window.location.href.includes(step),
    );
    const nextStep = currentStep >= 0 ? authSteps[currentStep + 1] : "/";
    window.location.href = nextStep;
  }

  function goBack() {
    const currentStep = authSteps.findIndex((step) =>
      window.location.href.includes(step),
    );
    window.location.href = currentStep >= 1 ? authSteps[currentStep - 1] : "/";
  }

  function checkUserAuthStatus(redirect = true) {
    if (!isLoading) {
      setIsLoading(true);

      const { pathname, search, hash } = window.location;
      const redirection = sanitizeRedirectPath(`${pathname}${search}${hash}`);
      const isLoginPage = pathname.includes("/login");

      const storedApiRegion = sessionStorage.getItem("apiRegion");
      apiUserClient
        .userDetails()
        .then((userData) => {
          setIsLoading(false);
          if (userData) {
            setCurrentUser(userData);
            setApiRegion(storedApiRegion ?? userData.api_region ?? "ca");
          } else {
            setCurrentUser(null);
            if (!isLoginPage) {
              setRedirectionPath(redirection);
            }

            if (redirect) {
              window.location.href = `/login${search}`;
            }
          }
        })
        .catch((err) => {
          setIsLoading(false);
          setCurrentUser(null);
          if (!isLoginPage) {
            setRedirectionPath(redirection);
          }

          if (redirect) {
            window.location.href = `/login${search}`;
          }
        });
    }
  }

  function setPassword(token: string, password: string) {
    return apiUserClient.setPassword(token, password).then((data) => {
      setMFAUserID(data.user_id);
      setStytchMemberID(data.stytch_user_id);
      setIntermediateToken(data.intermediate_token);

      const steps: string[] = [];
      steps.push("/addphone");
      steps.push("/verifyphone");
      steps.push("/");
      setAuthSteps(steps);
      window.location.href = steps[0];
    });
  }

  function login(email: string, password: string) {
    return postAuthLoginRs({
      body: { username: email, password },
    })
      .then((res) => {
        const data = res.data;
        const steps: string[] = [];
        if (data) {
          setMFAUserID(data.user_id);
          setMFAMethodID(data.stytch_organization_id);
          setStytchMemberID(data.stytch_user_id);
          setIntermediateToken(data.intermediate_token);

          if (data.requires_email_verification) {
            steps.push("/verifyemail");
          }
          if (!data.phone_number) {
            steps.push("/addphone");
            steps.push("/verifyphone");
          } else if (data.requires_mfa) {
            setRedactedNumber(data.phone_number);
            steps.push("/confirmotp");
          }

          if (data.has_temp_pw) {
            steps.push("/changepassword");
          }
          if (data.profile_update_required) {
            steps.push(`/users/${data.user_id}/profile/edit`);
          }

          steps.push(sanitizeRedirectPath(redirectionPath));
          setAuthSteps(steps);
          setRedirectionPath("");
          window.location.href = steps[0];
        } else {
          setCurrentUser(null);
          window.location.href = "/login";
        }
      })
      .catch((err) => {
        setCurrentUser(null);
        throw err;
      });
  }

  function logout() {
    postAuthLogoutRs()
      .then(() => {
        setCurrentUser(null);
        window.location.href = "/login";
      })
      .catch(() => {
        setCurrentUser(null);
        window.location.href = "/login";
      });
  }

  function addPhoneNumber(phoneNumber: string) {
    return postAuthAddPhoneRs({
      body: {
        phone_number: phoneNumber,
        user_id: mfaUserID ?? "",
        intermediate_token: intermediateToken ?? "",
      },
    }).then((res) => {
      const data = res.data;
      if (!data) {
        return;
      }
      setMFAMethodID(data.phone_id);
      setRedactedNumber(data.phone_number);
      goToNextStep();
    });
  }

  function verifyOTP(code: string) {
    return postAuthVerifyOtpRs({
      body: {
        otp_code: code,
        stytch_organization_id: mfaMethodID ?? "",
        stytch_user_id: stytchMemberID ?? "",
        intermediate_token: intermediateToken ?? "",
      },
    }).then(() => {
      goToNextStep();
    });
  }

  function resendOTP() {
    return postAuthResendCodeRs({
      body: {
        user_id: mfaUserID ?? "",
        intermediate_token: intermediateToken ?? "",
      },
    }).then((res) => {
      if (res.data) {
        setMFAMethodID(res.data.mfa_method_id);
      }
    });
  }

  function verifyPasswordStrength(password: string) {
    return apiUserClient.verifyPasswordStrength(password);
  }

  const getOrgIDFromURL = () => {
    const url = new URL(window.location.href);
    return url.pathname.split("/")[2];
  };

  const refreshUserGroups = async () => {
    try {
      const res = await getUserMeGroups();
      if (res.data) {
        // getUserMeGroups returns GroupResourceWithRole[] (role: string),
        // while the app-wide currentUserGroups is IAlertGroup[] (role is a
        // literal union). Runtime data is identical; cast at this boundary
        // rather than re-typing IAlertGroup across the whole app.
        const userGroups = (res.data.items ?? []) as unknown as IAlertGroup[];
        setCurrentUserGroups(userGroups);
        const foundAgency = userGroups.find(
          (agency) => agency.is_police_service,
        );
        if (foundAgency) {
          setCurrentUserAgency(foundAgency);
        }
      }
    } catch (error) {
      console.error("Failed to refresh user groups:", error);
    }
  };

  return (
    <AuthStateContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        currentUserAgency,
        setCurrentUserAgency,
        currentUserGroups,
        setCurrentUserGroups,
        mfaUserID,
        stytchMemberID,
        intermediateToken,
        redactedNumber,
        setRedactedNumber,
        setMFAUserID,
        setMFAMethodID,
        authSteps,
        checkUserAuthStatus,
        login,
        logout,
        setPassword,
        goBack,
        addPhoneNumber,
        verifyOTP,
        resendOTP,
        verifyPasswordStrength,
        redirectionPath,
        setRedirectionPath,
        getOrgIDFromURL,
        checkPermission: checkUserPermission,
        refreshUserGroups,
      }}
    >
      {children}
    </AuthStateContext.Provider>
  );
};

export { AuthStateContext, AuthStateProvider };
